import { Suspense, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import withSession from '@/features/session/component/Session';
import routes from '@/app/routes';
import { Spinner } from '@yaradigitallabs/ahua-react';

import type { User } from './features/session/types/session.types';
import { MicrofrontendDetector } from './utils/MicroFrontendDetector';
import { Header } from './components/ui/Header';
import { config } from './config';
import LoginPage from './pages/login';
import AuthRoute from './components/AuthRoute';
import Layout from './pages';
import { useAppSelector } from './app/hooks';
import ErrorPage from './pages/error';
import FormulaMate from './features/formulaMate/ConnectedFormulaMate';
import { FormulaMateProvider } from './features/formulaMate/FormulaMateContext';

interface AppProps {
  signIn: () => void;
  signOut: () => void;
  user: User | null;
}

const App = ({ signIn, signOut, user }: AppProps) => {
  const isMicrofrontend = useMemo(() => {
    return MicrofrontendDetector.isMicrofrontend();
  }, []);
  const isAuthenticated = !!user;
  const { responseErrorStatus } = useAppSelector(state => state.session);

  if (
    responseErrorStatus == 401 ||
    responseErrorStatus == 404 ||
    responseErrorStatus == 403
  ) {
    return (
      <ErrorPage
        errorStatusCode={responseErrorStatus}
        isMicroFrontend={isMicrofrontend}
      />
    );
  }
  return (
    <FormulaMateProvider key={user?.email ?? 'signed-out'}>
      <div className={'w-full h-full'}>
        {!isMicrofrontend && user && (
          <Header title={config.appName} signOut={signOut} />
        )}
        <Suspense fallback={<Spinner />}>
          <Routes>
            <Route
              path={routes.LOGIN}
              element={
                !isAuthenticated ? (
                  <LoginPage signIn={signIn} />
                ) : (
                  <Navigate to="/list-tags" />
                )
              }
            />
            <Route
              path={routes.LAYOUT}
              element={
                <AuthRoute isAuthenticated={isAuthenticated}>
                  <Layout isAuthenticated={isAuthenticated} />
                </AuthRoute>
              }
            />
          </Routes>
        </Suspense>
        {isAuthenticated && <FormulaMate key={user.email} />}
      </div>
    </FormulaMateProvider>
  );
};

export default withSession(App);
